import { useEffect, useState, type ReactNode } from 'react';
import type { IThemeState } from '@vanialabs/ui-engine';
import { themeEngine } from '../bootstrap/themeEngine.instance';
import { ThemeContext, type IThemeContextValue } from './ThemeContext';
import { applyThemeCssVariables } from './cssVariableBridge';

interface ThemeProviderProps {
  children: ReactNode;
}

function toContextValue(state: IThemeState): IThemeContextValue {
  return {
    theme: state.activeTheme,
    isLoading: state.isLoading,
    fellBackToDefault: state.fellBackToDefault,
  };
}

/**
 * Subscribes to the app-wide ThemeEngine instance (see
 * bootstrap/themeEngine.instance.ts) and republishes its state as React
 * context.
 *
 * This is the ONLY component in the app that talks to ThemeEngine
 * directly. Everything else — components, pages, features — must go
 * through useTheme(). This mirrors the engine's own rule that "Core never
 * learns which theme is active by name unless it explicitly asks."
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [value, setValue] = useState<IThemeContextValue>(() => toContextValue({
    activeTheme: null,
    activeThemeId: null,
    isLoading: true,
    fellBackToDefault: false,
  }));

  useEffect(() => {
    console.log('[TRACE] ThemeProvider: subscribing to themeEngine', themeEngine);

    const unsubscribe = themeEngine.subscribe((state) => {
      console.log('[TRACE] ThemeProvider: subscribe callback fired', {
        hasActiveTheme: Boolean(state.activeTheme),
        activeThemeId: state.activeThemeId,
        isLoading: state.isLoading,
        fellBackToDefault: state.fellBackToDefault,
      });

      setValue(toContextValue(state));

      if (state.activeTheme) {
        console.log('[TRACE] ThemeProvider: calling applyThemeCssVariables');
        applyThemeCssVariables(state.activeTheme);
      } else {
        console.warn('[TRACE] ThemeProvider: subscribe fired with NO activeTheme — CSS vars will not be applied this cycle');
      }
    });

    // Kicks off the boot sequence: resolves a preferred/default theme id
    // and activates it. ThemeEngine guarantees a safe result (fallback to
    // default) — this call is fire-and-forget from the provider's point
    // of view; state updates arrive via the subscription above.
    console.log('[TRACE] ThemeProvider: calling themeEngine.initialize()');
    Promise.resolve(themeEngine.initialize())
      .then(() => {
        console.log('[TRACE] ThemeProvider: initialize() resolved');
      })
      .catch((err) =>
        console.error(
          '[TRACE] ThemeProvider: initialize() rejected',
          err
        )
      );

    return () => {
      console.log('[TRACE] ThemeProvider: unsubscribing (cleanup ran — check if this fires unexpectedly in prod)');
      unsubscribe();
    };
  }, []);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
